import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";
import { getAppleIdToken, getGoogleAccessToken, getMicrosoftIdToken } from "lib/socialAuth";

const resolveRedirectUriForCurrentHost = (configured, fallbackPath) => {
  const fallback = `${window.location.origin}${fallbackPath}`;
  const value = String(configured || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.host !== window.location.host) {
      return `${window.location.origin}${parsed.pathname || fallbackPath}`;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
};

const hasMsalInteractionMarker = () => {
  const storages = [
    typeof window !== "undefined" ? window.sessionStorage : null,
    typeof window !== "undefined" ? window.localStorage : null,
  ];
  for (const storage of storages) {
    if (!storage) continue;
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i) || "";
        if (key === "msal.interaction.status" || key.endsWith(".interaction.status")) {
          return true;
        }
      }
    } catch {
      // ignore
    }
  }
  return false;
};

function safeNextPath(value) {
  const next = String(value || "").trim();
  if (!next.startsWith("/")) return "";
  if (next.includes("/authentication/") || next.endsWith("/sign-out")) return "";
  return next;
}

/**
 * @param {{ mode?: 'portal' | 'staff' }} options
 * portal → /account (storefront customers), staff → /post-login (VBS staff picker)
 */
export default function useStorefrontSignIn(options = {}) {
  const { mode = "portal" } = options;
  const storefront = getStorefrontConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithSocial, token } = useAuth();

  const isMobile =
    location.pathname === "/m/authentication/sign-in" ||
    location.pathname.startsWith("/m/");

  const queryNext = safeNextPath(searchParams.get("next"));
  const stateNext = safeNextPath(location.state?.from?.pathname);
  const savedNext = (() => {
    try {
      return safeNextPath(sessionStorage.getItem("postLoginNext"));
    } catch {
      return "";
    }
  })();

  const defaultRedirect =
    mode === "staff"
      ? isMobile
        ? "/m/post-login"
        : "/post-login"
      : isMobile
        ? "/m/account"
        : "/account";

  const redirectTo = queryNext || stateNext || savedNext || defaultRedirect;

  const googleClientId = String(
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
      process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ||
      "",
  ).trim();
  const appleClientId = String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
  const microsoftClientId = String(
    process.env.REACT_APP_MICROSOFT_CLIENT_ID || "",
  ).trim();
  const microsoftTenantId =
    String(process.env.REACT_APP_MICROSOFT_TENANT_ID || "common").trim() ||
    "common";
  const appleRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_APPLE_REDIRECT_URI,
    "/authentication/sign-in",
  );
  const microsoftRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    "/msal-popup.html",
  );

  const isPopupWindow = typeof window !== "undefined" && !!window.opener;
  const msalSearch =
    typeof window !== "undefined" ? String(window.location.search || "") : "";
  const msalHash =
    typeof window !== "undefined" ? String(window.location.hash || "") : "";
  const hasMsalCallbackPayload = /(code=|id_token=|access_token=|error=|state=)/i.test(
    `${msalSearch}&${msalHash}`,
  );
  const isPopupOnSignInRoute =
    isPopupWindow && location.pathname.endsWith("/authentication/sign-in");
  const isMsalPopupCallback =
    isPopupOnSignInRoute ||
    (isPopupWindow && (hasMsalCallbackPayload || hasMsalInteractionMarker()));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const from = queryNext || stateNext;
    if (from) {
      try {
        sessionStorage.setItem("postLoginNext", from);
      } catch {
        // ignore
      }
    }
  }, [queryNext, stateNext]);

  useEffect(() => {
    if (isMsalPopupCallback) return;
    if (token) navigate(redirectTo, { replace: true });
  }, [isMsalPopupCallback, token, redirectTo, navigate]);

  const handleEmailLogin = async () => {
    try {
      setLoading(true);
      setError("");
      const ok = await login(String(email || "").trim(), password);
      if (ok) navigate(redirectTo, { replace: true });
      else setError("Invalid email or password.");
    } catch {
      setError("Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider) => {
    const normalized = String(provider || "").trim().toLowerCase();
    if (!normalized) return;
    const providerEnabled =
      (normalized === "google" && !!googleClientId) ||
      (normalized === "apple" && !!appleClientId) ||
      (normalized === "microsoft" && !!microsoftClientId);
    if (!providerEnabled) {
      setError(`${normalized} sign in is not configured yet.`);
      return;
    }
    try {
      setLoading(true);
      setError("");
      let payload = {};
      if (normalized === "google") {
        payload = await getGoogleAccessToken({ clientId: googleClientId });
      } else if (normalized === "apple") {
        payload = await getAppleIdToken({
          clientId: appleClientId,
          redirectURI: appleRedirectUri,
        });
      } else if (normalized === "microsoft") {
        payload = await getMicrosoftIdToken({
          clientId: microsoftClientId,
          tenantId: microsoftTenantId,
          redirectUri: microsoftRedirectUri,
        });
      }
      const ok = await loginWithSocial({
        provider: normalized,
        ...payload,
        companyKey: storefront?.companyKey || undefined,
      });
      if (ok) navigate(redirectTo, { replace: true });
      else setError("Social sign in failed.");
    } catch (socialError) {
      const details =
        socialError?.response?.data?.error?.message ||
        socialError?.response?.data?.error ||
        socialError?.response?.data?.message ||
        socialError?.message ||
        "Social sign in failed.";
      setError(String(details));
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    loading,
    error,
    handleEmailLogin,
    handleSocialSignIn,
    isMsalPopupCallback,
    googleClientId,
    appleClientId,
    microsoftClientId,
    registerPath: isMobile ? "/m/register" : "/register",
    isMobile,
  };
}
